import SmartButton from '@mui/icons-material/SmartButton'
import type { BoxProps } from '@mui/material'
import { Box, ToggleButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { EntityNotes } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { IconProps } from '@tunasong/icons'
import {
  AccountTree,
  Activities,
  Context,
  Debug,
  Folder,
  Share as Graph,
  Note as Notes,
  Pool,
  Settings,
  Suggestion,
} from '@tunasong/icons'
import { isCollabEntity, isPersistedEntity, logger, matchAny } from '@tunasong/models'
import { ChatBadge, useChat } from '@tunasong/plugin-chat/ui'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isChat, isPage, isSong } from '@tunasong/schemas'
import type { SidebarHandler } from '@tunasong/ui-lib'
import { Divider, HBox, VBox, isDevelopment, useElementRef, useSidebar } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react'
import invariant from 'tiny-invariant'
import { SidebarActivities } from './sidebar-activities.js'
import { SidebarChat } from './sidebar-chat.js'
import { SidebarContent } from './sidebar-content.js'
import { SidebarContext } from './sidebar-context.js'
import { SidebarDebug } from './sidebar-debug.js'
import { SidebarEntity } from './sidebar-entity.js'
import { SidebarEvents } from './sidebar-events.js'
import { SidebarGraph } from './sidebar-graph.js'
import { SidebarPool } from './sidebar-pool.js'
import { SidebarSettings } from './sidebar-settings.js'
import { SidebarSuggestion } from './sidebar-suggestion.js'

export interface SidebarProps extends BoxProps {
  className?: string
  entity?: Persisted<Entity>
}

const Sidebar: FC<SidebarProps> = props => {
  const { entity, sx, ...restProps } = props

  const { activeTool, setActiveTool } = useSidebar()

  const { activeComments, isLoading: isChatLoading } = useChat({ element: entity })
  /** If we have a ?ref in the URL, we open the chat if a comment has the same ID */
  const ref = useElementRef()
  useEffect(() => {
    const hasRefComment = ref && activeComments.find(c => c.id === ref)
    if (hasRefComment) {
      /** Chat is tool #1 */
      setActiveTool('Chat')
    }
  }, [activeComments, ref, setActiveTool])

  /** If activeComments change, we automatically open the chat tool */
  const prevActiveComments = useRef<number>(0)
  useEffect(() => {
    if (prevActiveComments.current > 0 && prevActiveComments.current < activeComments.length && !isChatLoading) {
      logger.debug('Setting active', prevActiveComments, activeComments)
      setActiveTool('Chat')
    }
    prevActiveComments.current = activeComments.length
  }, [activeComments, isChatLoading, setActiveTool])

  const handleUpdate = graphHooks.useEntityUpdate({ debounceDelay: 3 * 1000 })
  const handleEntityChange = (partial: Partial<Entity>) => {
    invariant(isPersistedEntity(entity), 'Entity to be updated must be an element')
    handleUpdate(entity.id, partial)
  }

  const numComments = activeComments.length
  const ChatIcon = useMemo(
    () => (props: IconProps) => <ChatBadge numComments={numComments} {...props} />,
    [numComments]
  )

  const tools: SidebarHandler[] = [
    {
      name: `Settings`,
      Icon: Settings,
      Component: SidebarSettings,
      disabled: () => false,
    },
    {
      name: 'Chat',
      Icon: ChatIcon,
      Component: SidebarChat,
      disabled: () => isChat(entity),
    },
    {
      name: 'Activities',
      description: 'Summary of recent activities',
      Icon: Activities,
      Component: SidebarActivities,
      disabled: () => false,
    },
    {
      name: 'Notes',
      description:
        'Notes are personal to you. Write down your e.g., performance notes here. Notes will be shown in the performance view.',
      Icon: Notes,
      Component: EntityNotes,
      disabled: () => false,
    },
    {
      name: 'Event Commands',
      description:
        'Commands to run for different events. For example, you can run a command when a the page is opened.',
      Icon: SmartButton,
      Component: SidebarEvents,
      disabled: () => false,
    },

    {
      name: 'Pool',
      description: 'The Pool contains media that belongs to this item. Select media to insert it into the page.',
      Icon: Pool,
      Component: SidebarPool,
      disabled: (entity: Entity) => !matchAny(isSong, isPage)(entity),
    },
    {
      name: 'Context',
      description: 'The context shows the musical context. If you are in an editor your position will matter.',
      Icon: Context,
      Component: SidebarContext,
      disabled: (entity: Entity) => !isSong(entity),
    },
    {
      name: 'Suggestions',
      description: 'Suggestions for chords, notes or scales based on your current context',
      Icon: Suggestion,
      Component: SidebarSuggestion,
      disabled: (entity: Entity) => !isSong(entity),
    },

    // {
    //   name: 'Record',
    //   description: 'Record your performance',
    //   Icon: Microphone,
    //   Component: SidebarRecord,
    //   // @todo move these into the plugins
    //   disabled: (entity: Entity) => !(isSong(entity) || isPage(entity) || isFolder(entity)),
    // },
    // {
    //   name: 'Guitar',
    //   description: 'Guitar tools',
    //   Icon: Guitar,
    //   Component: SidebarGuitar,
    //   disabled: (entity: Entity) => !isSong(entity),
    // },
    isDevelopment()
      ? {
          name: 'Debug',
          description: 'Debug information',
          Icon: Debug,
          Component: SidebarDebug,
          disabled: (entity: Entity) => !isCollabEntity(entity),
        }
      : null,
    isDevelopment()
      ? {
          name: 'Graph',
          description: 'Show the outgoing and incoming links in the content graph.',
          Icon: Graph,
          Component: SidebarGraph,
          disabled: (entity: Entity) => !entity,
        }
      : null,
    isDevelopment()
      ? {
          name: 'Debug Content',
          description: 'All child content',
          Icon: Folder,
          Component: SidebarContent,
          disabled: () => false,
        }
      : null,
    isDevelopment()
      ? {
          name: 'Entity Content',
          description: 'Entity Details',
          Icon: AccountTree,
          Component: SidebarEntity,
          disabled: () => false,
        }
      : null,
  ].filter(Boolean) as SidebarHandler[]

  /** When the entity is not set we collapse the toolbar */
  useEffect(() => {
    if (!entity) {
      setActiveTool(null)
      return
    }
    /** If there is an active tool, and it's disabled, we also collapse */
    if (activeTool && tools.find(t => t.name === activeTool)?.disabled(entity)) {
      setActiveTool(null)
    }
  }, [activeTool, entity, setActiveTool, tools])

  const toggleActive = useCallback(
    (num: number) => () => {
      if (tools.findIndex(tool => tool.name === activeTool) === num) {
        setActiveTool(null)
      } else {
        setActiveTool(tools[num].name)
      }
    },
    [activeTool, setActiveTool, tools]
  )

  const theme = useTheme()
  const xl = useMediaQuery(theme.breakpoints.up('xl'))
  const tiny = useMediaQuery(theme.breakpoints.down('sm'))

  const handler = activeTool !== null ? tools.find(t => t.name === activeTool) : null
  const ActiveComponent = handler ? handler.Component : null
  const showSidebarIcons = !tiny || (tiny && ActiveComponent)

  // 100vw is a hack to make the sidebar full width on mobile
  const containerWidth = tiny && activeTool ? '100vw' : 'auto'

  const toolWidth = activeTool ? (xl ? 450 : 350) : 0

  return (
    <HBox
      sx={{
        ...sx,
        width: containerWidth,
        backgroundColor: theme.vars.palette.layers,
        transition: theme.transitions.create('width'),
        zIndex: 1,
        borderLeft: theme => `solid 1px ${theme.vars.palette.divider}`,
        // reset coordinate system with a transform
        transform: 'translateZ(0)',
      }}
      {...restProps}
    >
      <VBox
        sx={{
          /** Slide in the sidebar */
          transition: 'all 0.25s',
          width: toolWidth,
          opacity: activeTool ? 1 : 0,
          pb: activeTool ? 2 : 0,
          px: activeTool ? 1 : 0,
          overflow: 'auto',
          flex: 1,
        }}
      >
        {ActiveComponent && entity && (
          <Fragment key={entity.id}>
            <Box>
              <Divider>{handler?.name}</Divider>
            </Box>
            {handler?.description ? (
              <>
                <Typography variant="caption" gutterBottom>
                  {handler?.description}
                </Typography>
              </>
            ) : null}
            <ActiveComponent key={entity?.id} entity={entity} onEntityChange={handleEntityChange} />
          </Fragment>
        )}
      </VBox>
      {showSidebarIcons ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {tools.map(({ name, disabled, Icon }, idx) => (
            <ToggleButton
              key={idx}
              sx={{
                border: 'none !important',
                borderRadius: 0,
                p: 2,
                color: theme => theme.vars.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme => theme.vars.palette.primary.main,
                },
              }}
              disabled={!entity || disabled(entity)}
              title={`${name}`}
              value={name}
              selected={activeTool === tools[idx].name}
              onClick={toggleActive(idx)}
            >
              <Icon fontSize={'medium'} />
            </ToggleButton>
          ))}
        </Box>
      ) : null}
    </HBox>
  )
}

export default Sidebar
